import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { cacheControlHeader } from '@/utils/constants-platform-specific';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';

// BE caps limit at 100; sport count is far below it
const SPORTS_PAGE_LIMIT = 100;

export const createSportsKeys = (product: Product) => {
    const all = [product, 'sports'] as const;
    return { all };
};

export type SportsKeys = ReturnType<typeof createSportsKeys>;

export const useSportsKeys = (): SportsKeys => {
    const product = useJurisdictionStore(state => state.product);
    return useMemo(() => createSportsKeys(product), [product]);
};

export type SportLeagueStatus = 'active' | 'offseason';

export type SportLeague = {
    id: string;
    name: string;
    logo_url: string | null;
    status: SportLeagueStatus;
    event_count: number;
    live_event_count: number;
};

export type SportWithLeagues = {
    id: string;
    name: string;
    icon_url: string | null;
    leagues: SportLeague[];
};

export type GetSportsResponse = {
    data: SportWithLeagues[];
};

export const getSports = async (): Promise<GetSportsResponse> => {
    const queryParams = new URLSearchParams({ limit: SPORTS_PAGE_LIMIT.toString() });
    const response = await fetch(`${URLS.CHAMELON_API_URL}/sports?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            ...cacheControlHeader,
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

type SportsQueryOptions = Omit<
    UseQueryOptions<GetSportsResponse, Error, SportWithLeagues[]>,
    'queryKey' | 'queryFn' | 'select'
>;

export const useSports = (queryOptions?: SportsQueryOptions) => {
    const sportsKeys = useSportsKeys();
    return useQuery({
        queryKey: sportsKeys.all,
        queryFn: getSports,
        select: data => data.data,
        ...queryOptions,
    });
};
